import { Box, BoxProps } from "@chakra-ui/react";
import React from "react";

/**
 * BasePageFrame - Unified foundation for all page layouts
 *
 * Provides shared styling foundation for both public and private pages:
 * - Consistent minimum height and background colors
 * - Unified spacing and typography tokens
 * - Flexible enough to support both full-width (private) and max-width (public) layouts
 *
 * This component is the single source of truth for page-level styling.
 * It's extended by MainContainerPrivate and WithPublicPage.
 */

export interface BasePageFrameProps extends Omit<BoxProps, "as"> {
  as?: React.ElementType;
  children: React.ReactNode;
  /**
   * Variant determines the styling mode:
   * - "private": Full-width app layout with specific padding for private pages
   * - "public": Content-constrained marketing layout with max-width centering
   */
  variant: "public" | "private";
  /**
   * Maximum width for public variant (ignored for private)
   * @default "8xl"
   */
  maxW?: BoxProps["maxW"];
  /**
   * Center the content horizontally (public variant only)
   * @default true
   */
  centered?: boolean;
  /**
   * Allow full-bleed backgrounds with inner content constraint (public variant only)
   * @default false
   */
  fullBleed?: boolean;
}

export default function BasePageFrame({
  as = "main",
  children,
  variant,
  maxW = "8xl",
  centered = true,
  fullBleed = false,
  bg,
  color,
  minH,
  px,
  py,
  pt,
  pb,
  fontSize,
  ...rest
}: BasePageFrameProps) {
  // Private variant: simple full-width container
  if (variant === "private") {
    return (
      <Box
        as={as}
        width="full"
        bg={bg ?? "moveon.bg"}
        color={color ?? "fg"}
        minH={minH ?? "dvh"}
        // pt={8} // min height off the top navbar
        pt={pt ?? 12}
        px={
          px ?? {
            base: "2",
            sm: "4",
            md: "12",
            lg: "24",
          }
        }
        pb={pb ?? "24"}
        fontSize={fontSize}
        {...rest}
      >
        {children}
      </Box>
    );
  }

  // Public variant with full-bleed support
  if (fullBleed) {
    return (
      <Box
        as={as}
        width="full"
        px={0}
        py={py ?? { base: "0", sm: "2", md: "4", lg: "8", xl: "12" }}
        pt={pt ?? { base: 8, md: 16 }}
        fontSize={fontSize ?? { base: "sm", md: "md" }}
        color={color ?? "moveon.publicFont"}
        bg={bg}
        minH={minH}
        {...rest}
      >
        <Box
          maxW={maxW}
          mx={centered ? "auto" : undefined}
          px={px ?? { base: 4, md: 8 }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  // Public variant: standard max-width centered container
  return (
    <Box
      as={as}
      maxW={maxW}
      mx={centered ? "auto" : undefined}
      px={px ?? { base: 4, md: 8 }}
      py={py ?? { base: "0", sm: "2", md: "4", lg: "8", xl: "12" }}
      pt={pt ?? { base: 8, md: 16 }}
      fontSize={fontSize ?? { base: "sm", md: "md" }}
      color={color ?? "moveon.publicFont"}
      bg={bg}
      minH={minH}
      {...rest}
    >
      {children}
    </Box>
  );
}
